import { motion } from "framer-motion";
import { SiteLink } from "./SiteLink";

export function ParadigmManifestoSection() {
  return (
    <section className="border-t border-zinc-100 dark:border-zinc-800 bg-white dark:bg-black">
      <div className="mx-auto mt-12 flex max-w-4xl flex-col px-6 py-24 text-center sm:mt-16 sm:py-28 md:mt-20 md:py-32">
        {/* Label */}
        <p className="text-[10px] font-semibold uppercase tracking-[0.26em] text-zinc-500 dark:text-zinc-400">
          Same goals. New platform. New product design paradigm.
        </p>

        {/* Headline (statement, not a question) */}
        <h2 className="mt-5 text-balance text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100 sm:text-3xl">
          When agents assemble user experiences, product builders still need to
          achieve outcomes.
        </h2>

        {/* Narrative body */}
        <motion.div
          className="mt-7 space-y-4 text-left text-sm leading-relaxed text-zinc-700 dark:text-zinc-300 sm:mt-8 sm:text-[15px]"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <p className="mb-4">
            For a long time, UI was the product. We drew screens, linked them
            together, and empowered users to click their way toward the outcome.
            Product builders meticulously designed every path end-to-end.
          </p>

          {/* Visual line break */}
          <div className="flex justify-center">
            <div className="h-0.5 w-20 rounded-full bg-zinc-300 dark:bg-zinc-700 opacity-90 my-3" />
          </div>

          <p className="mt-4">
            <span className="font-medium">The world is changing.</span> Apps are
            becoming component-based, serving as pieces of agent-assembled
            experiences. Users express intent and agents translate it into
            actions.
          </p>

          <p>
            This shift creates opportunities for extraordinary user experiences.
            It also creates risk.
          </p>

          {/* Visual line break (second, with even spacing) */}
          <div className="flex justify-center">
            <div className="h-0.5 w-20 rounded-full bg-zinc-300 dark:bg-zinc-700 opacity-90 my-3" />
          </div>

          <p className="mt-4">
            As products become reactive, builders lose control. Without a new
            primitive, we lose the ability to proactively design experiences
            around outcomes.
          </p>

          <p>
            <span className="font-medium">
              Non-deterministic flows are that primitive.
            </span>{" "}
            They let you design journeys the agent can take without hardcoding
            every path or freezing the UI in place. The agent still decides what
            to show next, but you influence the decisions.
          </p>

          <p>
            Taias believes in a world where those building for the end-users
            have the power to shape their experiences. We exist to help you turn
            your MCP server's tools, resources, and prompts into a cohesive
            product experience that drives outcomes.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-10 flex justify-center"
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ delay: 0.1, duration: 0.5, ease: "easeOut" }}
        >
          <SiteLink
            href="/docs"
            className="inline-flex items-center justify-center rounded-full bg-black dark:bg-white px-6 py-2.5 text-xs font-semibold text-white dark:text-black shadow-md shadow-black/10 dark:shadow-white/10 transition hover:-translate-y-0.5 hover:bg-zinc-900 dark:hover:bg-zinc-100 sm:text-sm"
          >
            I'm ready
          </SiteLink>
        </motion.div>
      </div>
    </section>
  );
}
